import {
  expandElements,
  useExpandedItems,
} from '@client/hooks/use-expand-elements.hooks';
import {
  useFlatSortFilteredElements,
  useFilteredElementChildren,
  useElementSearchString,
  useSortElementsBy,
  useSortElementsDirection,
} from '@client/hooks/use-filter-elements.hooks';
import {
  getSelectedVersion,
  useSelectedElementId,
  useSelectedVersion,
  useSelectedVersionProductsAndStoreProducts,
} from '@client/store/ui';
import { useResolvedVersion } from '@client/hooks/use-resolved-version.hooks';
import { SharedIntersectionObserverProvider } from '@client/hooks/use-intersection-observer.hooks';
import ExpressionInputPanel from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import { Box } from '@mui/material';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import {
  getPathToElement,
  getElementById,
} from '@shared/helpers/recursive-element.helpers';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import {
  useElementListColumnWidth,
  useElementListColumnResizeHandle,
  ELEMENT_LIST_RESIZE_HANDLE_CLASS,
  ELEMENT_LIST_WIDTH_VAR,
  ELEMENT_LIST_WIDTH_CSS,
  elementListResizeHandleHoverSx,
} from '@features/proposals/ElementListRow/element-list-column-resize.hook';

import { MainLayout } from '@ui/layout';
import ProposalsOverview from '@features/proposals/ProposalsOverview';
import { hasVisibleProposalTarget } from '@features/proposals/proposals-overview-bar-layout.helpers';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import { ElementListMaxValuesProvider } from '@features/proposals/use-proposals.hooks';
import { ElementListGroupBarScaleProvider } from '@features/proposals/use-element-list-group-bar-scale.hooks';
import ElementTreeRow from '@features/proposals/ElementListRow/ElementTreeRow';
import SbefGroupRow from '@features/proposals/ElementListRow/SbefGroupRow';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import {
  ElementListHeader,
  ElementListFooter,
  ElementListTree,
  ELEMENT_LIST_HEADER_HEIGHT,
  ELEMENT_LIST_FOOTER_HEIGHT,
  FOCUS_VIEW_HEADER_HEIGHT,
} from '@features/proposals/ElementListRow';
import {
  LIST_SCROLL_HEIGHT_VAR,
  trackListScrollHeight,
} from '@features/proposals/ElementListRow/element-list-scroll.helpers';
import { useElementListSearchField } from '@features/proposals/ElementListRow/use-element-list-search-field.hook';
import { BuildRightPanel } from './BuildRightPanel';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getElementGroupingHelpers } from '@features/elements/element-grouping.model';
import { FocusViewHeader } from './FocusViewHeader';
import {
  useFocusViewSegments,
  useFocusViewItems,
  useOpenFocusElement,
  useOpenFocusGroup,
  useFocusViewBackNavigation,
} from '@client/hooks/use-element-focus.hook';
import { useGroupSectionProps } from './element-groups.model';
import { useSortedElementGroups } from './use-sorted-element-groups.hook';
import {
  type ElementListTab,
  useElementListKeyboardNavigation,
} from './use-element-list-keyboard-navigation.hook';
import {
  useProductListCategoryGroups,
  useProductListGroups,
} from '@features/products/use-product-list.hooks';
import {
  filterProductListCategoryGroups,
  filterProductListGroups,
  sortProductListCategoryGroups,
  sortProductListGroups,
} from '@features/products/product-list.helpers';
import { useGroupByCategory } from '@features/products/group-by-category.store';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import {
  useProjectNavigation,
  usePageFromUrl,
  useDeselectElement,
} from '@client/hooks/use-router.hooks';
import { usePageNavigationHotkeys } from '@client/hooks/use-page-navigation-hotkeys.hook';
import {
  useSelectedProposalId,
  useSetSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import { useProposals } from '@features/proposals/hooks/use-proposals.hooks';
import { hasElementSelection } from '@features/proposals/helpers/proposal-selection.helpers';
import { useActionsForProposal } from '@features/proposals/hooks/use-actions-for-proposal.hooks';
import { ActionListRow } from '@features/proposals/ElementListRow/ActionListRow';
import { ELEMENT_LIST_ROW_HEIGHT } from '@features/proposals/ElementListRow/ElementListRow';
import { AppBarStyles } from '@client/style/style.constants';

import { Page } from '@client/store/ui/ui.model';
import { ELEMENT_LIST_PROPOSAL_ACTIONS_TAB_ENABLED } from '@shared/constants';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import ProductCategoryGroupRow from '@features/products/components/ProductCategoryGroupRow';
import ProductGroupItemRow from '@features/products/components/ProductGroupItemRow';
import {
  BuildViewCsvDropZone,
  ImportTabPanel,
  useElementImports,
  useImportSearchMatchCount,
} from '@features/csv-import';
import { useImportListKeyboardNavigationContext } from '@features/csv-import/use-import-list-visible-row-ids.hook';
import { useProjectState } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useLocation } from 'react-router-dom';
import { shouldLoadElementImports } from '@features/csv-import/element-imports-fetch.helpers';
import {
  getListSelection,
  getVisibleListRowIds,
} from '@features/csv-import/list-selection.store';
import { resolveBulkActionTargetIds } from '@features/csv-import/list-multi-select.helpers';
import { buildImportBackspaceChanges } from '@features/csv-import/import-backspace.helpers';
import { mergeSx } from '@client/helpers/mui.helpers';
import { BuildViewHeader, BUILD_VIEW_HEADER_HEIGHT } from './BuildViewHeader';

type ListFillerProps = {
  onClick: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Height of the header covering the top of this list's scroll viewport.
   * Defaults to the element list header; the focus view passes its own.
   */
  headerHeight?: number;
};

/**
 * Overscroll room below the last row (DEV-3423).
 *
 * Collapsing a row shrinks the scroll container's `scrollHeight`. When the
 * remaining scroll distance is smaller than the shrink, the browser clamps
 * `scrollTop` to the new maximum and every row slides down by the difference —
 * the row under the cursor jumps away. Slack below the list keeps that
 * remaining distance above any shrink: the collapsed subtree sits below the
 * row and counts toward the slack itself, cancelling out its own height. So
 * this holds no matter how large the collapsed subtree is.
 *
 * Sized so the topmost clickable row — flush under the sticky header — is
 * still covered, which is exactly the amount that leaves one row visible at
 * maximum scroll. Any less reintroduces the jump for rows near the top of the
 * viewport; any more just scrolls that last row up behind the header and
 * empties the screen.
 *
 * The scroll container is `MainLayout.Content`, a viewport minus the app bar.
 * The Build header is inside that scrollport and is replaced by the list
 * header once the latter becomes sticky, so it does not reduce the viewport.
 */
const getListOverscrollCss = (headerHeight: number) =>
  `calc(100vh - ${AppBarStyles.HEIGHT}px - ${headerHeight}px - ${ELEMENT_LIST_ROW_HEIGHT}px - ${ELEMENT_LIST_FOOTER_HEIGHT}px)`;

const ListFiller: React.FC<ListFillerProps> = ({
  onClick,
  headerHeight = ELEMENT_LIST_HEADER_HEIGHT,
}) => {
  const handleDragStart = useElementListColumnResizeHandle();

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        display: 'flex',
        minHeight: getListOverscrollCss(headerHeight),
        backgroundColor: 'warmSurface.main',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: ELEMENT_LIST_WIDTH_CSS,
          boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.1)',
        }}
      >
        <Box
          className={ELEMENT_LIST_RESIZE_HANDLE_CLASS}
          onMouseDown={handleDragStart}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 4,
            cursor: 'ew-resize',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Horizontal scroll wrapper for the list/table. Caps the table to the content
 * width and lets only the table scroll sideways in narrow-viewport edge cases,
 * instead of stretching the whole grid (proposals bar / header).
 */
const listScrollWrapperSx = {
  minWidth: 0,
  overflowX: 'auto',
  // Hide the X scrollbar chrome; the table is still scrollable (wheel/trackpad).
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

/**
 * Import file headers use position:sticky against `main`. Overflow here would
 * make this box a sticky containing block.
 */
const importListScrollWrapperSx = {
  minWidth: 0,
} as const;

const elementListGridSx = {
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  gridTemplateColumns: 'minmax(0, 1fr)',
  width: '100%',
  minWidth: 0,
  minHeight: '100%',
} as const;

const elementListColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
} as const;

const elementListFillSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
} as const;

export const ElementsView: React.FC = () => {
  const resizableWidth = useElementListColumnWidth();
  const target = useResolvedProjectTarget();
  const hasTarget = hasVisibleProposalTarget(target);
  const { project } = useProjectState('project');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [actionsTabActive, setActionsTabActive] = useState(false);
  // Source `listTab` directly from the URL (react-router) rather than the UI
  // store, so the tab flips in the same render pass as the URL change. The UI
  // store is synced from the URL via a post-commit effect, which otherwise
  // causes a wasted pre-flip render of the previous tab on every toggle.
  const selectedPage = usePageFromUrl();
  const selectedProposalId = useSelectedProposalId();
  const proposalActions = useActionsForProposal(selectedProposalId);

  // Deactivate actions tab automatically when the proposal is deselected
  const prevProposalId = usePrevious(selectedProposalId, {
    setInitial: true,
  });
  if (
    actionsTabActive &&
    prevProposalId !== undefined &&
    selectedProposalId === undefined
  ) {
    setActionsTabActive(false);
  }

  const urlTab: ElementListTab = ((): ElementListTab => {
    if (selectedPage === Page.Import) return 'imports';
    if (selectedPage === Page.Products) return 'products';
    return 'elements';
  })();
  /** Store version: actions tab and other paths that must read baseline `actions`. */
  const selectedVersion = useSelectedVersion();
  const importsState = useElementImports(project?.id ?? -1);
  const importListNavigation = useImportListKeyboardNavigationContext(
    project?.id ?? -1,
  );
  const applyProjectChanges = useApplyProjectChanges();
  const isReadonly = useIsReadonly();
  const { pathname } = useLocation();
  const toggleImportCandidateRowDiscard = useCallback(
    (rowId: string) => {
      if (isReadonly || !selectedVersion) return;
      const targets = resolveBulkActionTargetIds(
        rowId,
        new Set(getListSelection().selectedIds).add(rowId),
        getVisibleListRowIds(),
      );
      if (targets.length === 0) return;
      // Mapped rows unmap first; a second Backspace (no mapping) skips.
      const changes = buildImportBackspaceChanges(selectedVersion, targets);
      if (changes.length === 0) return;
      void applyProjectChanges(changes);
    },
    [applyProjectChanges, isReadonly, selectedVersion],
  );
  const showImportTab = useMemo(() => {
    // Public routes cannot list imports (SHARED_PROJECT org header 404s).
    if (!shouldLoadElementImports(project?.id ?? -1, pathname)) return false;
    const versionImportIds = selectedVersion?.imports?.importIds ?? [];
    if (importsState.status === 'ready') {
      // Scope to the version's importIds: imports are stored project-wide, so
      // the tab only shows for versions that reference at least one loaded file.
      const loaded = importsState.data.importsById;
      return versionImportIds.some((id) => loaded[id] !== undefined);
    }
    return versionImportIds.length > 0;
  }, [
    pathname,
    project?.id,
    importsState,
    selectedVersion?.imports?.importIds,
  ]);
  const showActionsTab =
    actionsTabActive &&
    !!selectedProposalId &&
    ELEMENT_LIST_PROPOSAL_ACTIONS_TAB_ENABLED &&
    urlTab !== 'imports';
  const listTab: ElementListTab = showActionsTab ? 'actions' : urlTab;
  const prevListTab = usePrevious(listTab);
  const navigateTo = useProjectNavigation();

  useEffect(() => {
    if (selectedPage !== Page.Import || showImportTab) return;
    navigateTo({ page: Page.Elements });
  }, [selectedPage, showImportTab, navigateTo]);
  const deselectElement = useDeselectElement();
  const handleDeselect = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      setSelectedGroupKey(null);
      deselectElement(event);
    },
    [deselectElement],
  );
  const handleDeselectGroup = useCallback(() => setSelectedGroupKey(null), []);
  const proposals = useProposals();
  const setSelectedProposalId = useSetSelectedProposalId();
  const savedScrollRef = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);
  /** Record-mode tree (proposal actions applied when a proposal is selected). */
  const version = useResolvedVersion();
  const selectedElementId = useSelectedElementId();
  const focusViewSegments = useFocusViewSegments();
  const focusViewId = focusViewSegments.at(-1)?.id;
  const focusViewItems = useFocusViewItems();
  const openFocusElement = useOpenFocusElement();
  const openFocusGroup = useOpenFocusGroup();
  const focusViewBack = useFocusViewBackNavigation();
  const [groupBy] = useElementListGroupBy();
  const [elementSearchString] = useElementSearchString();
  const filteredFlatElements = useFlatSortFilteredElements();
  // Sorted + filtered direct children of the building version (root level).
  // Used for both the grouped and ungrouped element list, so root rows follow
  // the active sort ('name' / 'co2' / 'cost' / 'position') just like the
  // child rows rendered through `ElementTreeRow`.
  const sortedRootElements = useFilteredElementChildren();

  const groupProps = useGroupSectionProps(groupBy);

  const sortBy = useSortElementsBy();
  const direction = useSortElementsDirection();
  const selectedLifecycles = useSelectedLifecycles();
  const groupByCategory = useGroupByCategory();
  const productCategoryGroups = useProductListCategoryGroups();
  const productGroups = useProductListGroups();
  const productLookup = useSelectedVersionProductsAndStoreProducts();

  const productGroupCategoryIconMap = useMemo(() => {
    const map = new Map<
      string,
      ReturnType<typeof getElementIconCategoryName>
    >();
    productCategoryGroups.forEach((cat) => {
      const icon = getElementIconCategoryName(cat.id);
      cat.elements.forEach((pg) => map.set(pg.id, icon));
    });
    return map;
  }, [productCategoryGroups]);

  const filteredProductCategoryGroups = useMemo(
    () =>
      filterProductListCategoryGroups(
        productCategoryGroups,
        elementSearchString,
        version?.products,
      ),
    [productCategoryGroups, elementSearchString, version?.products],
  );

  const filteredProductGroups = useMemo(
    () =>
      filterProductListGroups(
        productGroups,
        elementSearchString,
        version?.products,
      ),
    [productGroups, elementSearchString, version?.products],
  );

  const sortedProductCategoryGroups = useMemo(
    () =>
      sortProductListCategoryGroups(filteredProductCategoryGroups, sortBy, {
        direction,
        productLookup,
        lifecycles: selectedLifecycles,
      }),
    [
      filteredProductCategoryGroups,
      productLookup,
      selectedLifecycles,
      sortBy,
      direction,
    ],
  );

  const sortedProductGroups = useMemo(
    () =>
      sortProductListGroups(filteredProductGroups, sortBy, {
        direction,
        productLookup,
        lifecycles: selectedLifecycles,
      }),
    [
      filteredProductGroups,
      productLookup,
      selectedLifecycles,
      sortBy,
      direction,
    ],
  );

  // Whether the active tab has any rows under the current search query. Drives
  // the search field's click-outside auto-close: an empty draft or a no-match
  // query closes on outside click; an active, matching query stays open.
  const importSearchMatchCount = useImportSearchMatchCount();
  const hasSearchMatches = (() => {
    if (listTab === 'products') {
      return groupByCategory
        ? filteredProductCategoryGroups.length > 0
        : filteredProductGroups.length > 0;
    }
    if (listTab === 'actions') return true;
    if (listTab === 'imports') return importSearchMatchCount > 0;
    return filteredFlatElements.length > 0;
  })();

  const {
    isSearchOpen,
    openSearch,
    fieldProps: searchFieldProps,
  } = useElementListSearchField({ hasMatches: hasSearchMatches });

  const sortedElementGroups = useSortedElementGroups({
    groupProps,
    version,
    sortedRootElements,
    sortBy,
    direction,
  });

  const { set: setGroupExpanded } = useExpandedItems({
    defaultPrefix: groupBy,
  });

  const elementLookup = useMemo(() => {
    const ancestorIdsByElementId = new Map<string, string[]>();
    const elementById = new Map<string, ReturnType<typeof getElementById>>();
    if (!version) return { ancestorIdsByElementId, elementById };

    const collectAncestors = (
      elements: typeof version.elements,
      ancestorIds: string[],
    ) => {
      elements.forEach((child) => {
        if (!projectGuards.is.element(child)) return;
        ancestorIdsByElementId.set(child.id, ancestorIds);
        elementById.set(child.id, child);
        collectAncestors(child.elements, [...ancestorIds, child.id]);
      });
    };

    collectAncestors(version.elements, []);
    return { ancestorIdsByElementId, elementById };
  }, [version]);

  const isSearchActive = elementSearchString.trim().length > 0;

  const searchExpansion = useMemo(() => {
    const forceExpandedElementIds = new Set<string>();
    const forceExpandedGroupKeys = new Set<string>();
    if (!isSearchActive)
      return { forceExpandedElementIds, forceExpandedGroupKeys };

    const helpers = getElementGroupingHelpers(groupBy)?.helpers;
    filteredFlatElements.forEach((element) => {
      const ancestorIds =
        elementLookup.ancestorIdsByElementId.get(element.id) ?? [];
      ancestorIds.forEach((id) => forceExpandedElementIds.add(id));

      const rootParentId = ancestorIds[0] ?? element.id;
      const rootParent = elementLookup.elementById.get(rootParentId);
      if (!rootParent || !projectGuards.is.element(rootParent)) return;

      const groupKey = helpers?.getGroupKey(rootParent);
      if (groupKey) forceExpandedGroupKeys.add(groupKey);
    });

    return { forceExpandedElementIds, forceExpandedGroupKeys };
  }, [isSearchActive, filteredFlatElements, elementLookup, groupBy]);

  // Clear group selection when an element row is selected. `none` is not an element.
  useEffect(() => {
    if (!hasElementSelection(selectedElementId)) return;
    setSelectedGroupKey(null);
  }, [selectedElementId]);

  // Auto-open ancestors when selection changes — not when resolved version identity changes.
  useEffect(() => {
    if (!selectedElementId) return;
    const baselineVersion = getSelectedVersion();
    if (!baselineVersion) return;

    const resolvedVersion = getResolvedBuildingVersion(
      baselineVersion,
      selectedProposalId,
    );
    const selectedElement = getElementById(resolvedVersion, selectedElementId);
    if (!selectedElement) return;

    const path =
      getPathToElement(resolvedVersion, selectedElement.id, false) ?? [];
    expandElements(...path.filter(projectGuards.is.element));

    const helpers = getElementGroupingHelpers(groupBy)?.helpers;

    // get to root parent (if none, the selected element is itself the direct child of the version)
    const [_buildingVersion, rootParent = selectedElement] = path;
    const key = rootParent && helpers?.getGroupKey(rootParent);
    if (key) setGroupExpanded(key, true);
  }, [selectedElementId, selectedProposalId, groupBy, setGroupExpanded]);

  useEffect(() => {
    const container = document.querySelector<HTMLElement>('main');
    if (!container) return;
    const onScroll = () => {
      savedScrollRef.current = container.scrollTop;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  const handleTabChange = useCallback<(tab: ElementListTab) => void>(
    (tab) => {
      if (tab === 'actions') {
        if (!ELEMENT_LIST_PROPOSAL_ACTIONS_TAB_ENABLED) return;
        setActionsTabActive(true);
        return;
      }
      setActionsTabActive(false);
      const pageByTab: Record<Exclude<ElementListTab, 'actions'>, Page> = {
        elements: Page.Elements,
        products: Page.Products,
        imports: Page.Import,
      };
      navigateTo({ page: pageByTab[tab] });
    },
    [navigateTo],
  );

  useElementListKeyboardNavigation({
    selectedGroupKey,
    selectedElementId,
    filteredFlatElements,
    elementLookup,
    hasElementGroups: !!groupProps,
    sortedElementGroups,
    forceExpandedGroupKeys: searchExpansion.forceExpandedGroupKeys,
    setSelectedGroupKey,
    navigateTo,
    listTab,
    groupByCategory,
    sortedProductCategoryGroups,
    sortedProductGroups,
    proposals,
    proposalActions,
    selectedVersion,
    selectedProposalId,
    setSelectedProposalId,
    openSearch,
    handleTabChange,
    openFocusElement,
    openFocusGroup,
    focusViewBack,
    importListNavigation,
    toggleImportCandidateRowDiscard,
  });

  usePageNavigationHotkeys(['d', 'r']);

  useLayoutEffect(() => {
    if (prevListTab === undefined || prevListTab === listTab) return;
    const container = document.querySelector<HTMLElement>('main');
    if (!container) return;
    container.scrollTop = savedScrollRef.current;
  }, [listTab, prevListTab]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    return trackListScrollHeight(content);
  }, [listTab, selectedVersion?.id, focusViewId]);

  const elementListContent = (() => {
    if (listTab === 'imports') {
      return (
        <>
          {project ? <ImportTabPanel projectId={project.id} /> : null}
          <ListFiller onClick={handleDeselect} />
          <ElementListFooter />
        </>
      );
    }
    if (listTab === 'actions') {
      return (
        <>
          <ElementListTree aria-label="Actions">
            {selectedVersion &&
              proposalActions.map((action) => (
                <ActionListRow
                  key={action.id}
                  action={action}
                  version={selectedVersion}
                  selectedRow={selectedElementId ?? null}
                />
              ))}
          </ElementListTree>
          <ListFiller onClick={handleDeselect} />
          <ElementListFooter />
        </>
      );
    }

    if (listTab === 'products') {
      return (
        <>
          <ElementListTree aria-label="Products">
            {groupByCategory
              ? sortedProductCategoryGroups.map((categoryGroup, i) => (
                  <ProductCategoryGroupRow
                    key={categoryGroup.id}
                    categoryGroup={categoryGroup}
                    startDepth={1}
                    hideDivider={i === 0}
                    forceExpanded={isSearchActive ? true : undefined}
                  />
                ))
              : sortedProductGroups.map((productGroup) => (
                  <ProductGroupItemRow
                    key={productGroup.id}
                    productGroup={productGroup}
                    depth={1}
                    categoryIcon={productGroupCategoryIconMap.get(
                      productGroup.id,
                    )}
                    forceExpanded={isSearchActive ? true : undefined}
                  />
                ))}
          </ElementListTree>
          <ListFiller onClick={handleDeselect} />
          <ElementListFooter />
        </>
      );
    }

    return (
      <>
        <ElementListTree aria-label="Elements">
          {groupProps && version
            ? sortedElementGroups.map((group, i) => (
                <SbefGroupRow
                  key={group.key}
                  group={group}
                  startDepth={1}
                  selectedRow={selectedGroupKey ?? selectedElementId ?? null}
                  onSelect={setSelectedGroupKey}
                  hideDivider={i === 0}
                  forceExpandedElementIds={
                    searchExpansion.forceExpandedElementIds
                  }
                  forceExpandedGroupKeys={
                    searchExpansion.forceExpandedGroupKeys
                  }
                />
              ))
            : sortedRootElements
                .filter(projectGuards.is.element)
                .map((element) => (
                  <ElementTreeRow
                    key={element.id}
                    element={element}
                    depth={1}
                    selectedRow={selectedElementId ?? null}
                    onSelect={() => undefined}
                    forceExpandedElementIds={
                      searchExpansion.forceExpandedElementIds
                    }
                  />
                ))}
        </ElementListTree>
        <ListFiller onClick={handleDeselect} />
        <ElementListFooter />
      </>
    );
  })();

  return (
    <MainLayout
      panelRight={
        <MainLayout.SidePanel position="right" aria-label="Element">
          <BuildRightPanel
            groupSelected={sortedElementGroups.some(
              (group) => group.key === selectedGroupKey,
            )}
          />
        </MainLayout.SidePanel>
      }
    >
      <BuildViewCsvDropZone
        onImported={(importId) =>
          navigateTo({ page: Page.Import, elementId: importId })
        }
      />
      <MainLayout.Content
        sx={{
          backgroundColor: hasTarget ? 'warmSurface.main' : 'warmSurface.dark',
          '--version-controls-opacity': 0,
          '--version-controls-pointer-events': 'none',
          '--selected-version-background': 'transparent',
          '--selected-version-text-color': 'rgba(0,0,0,0.8)',
          '&:has(> header[aria-label="Build"]:hover), &:has([data-proposals-overview][data-version-options-hover-active]:hover)':
            {
              '--version-controls-opacity': 1,
              '--version-controls-pointer-events': 'auto',
              '--selected-version-text-color': 'rgba(0,0,0,0.8)',
            },
        }}
      >
        <BuildViewHeader />
        <ElementListMaxValuesProvider>
          <ElementListGroupBarScaleProvider>
            <Box
              ref={contentRef}
              style={
                {
                  [ELEMENT_LIST_WIDTH_VAR]: `${resizableWidth}px`,
                } as React.CSSProperties
              }
              sx={mergeSx(elementListGridSx, elementListResizeHandleHoverSx, {
                // The tracked height describes the whole scrollport. This grid
                // follows the Build header in normal flow, so subtract that
                // sibling or every scroll event would grow the page by 46px.
                minHeight: `max(calc(100% - ${BUILD_VIEW_HEADER_HEIGHT}px), calc(var(${LIST_SCROLL_HEIGHT_VAR}, 0px) - ${BUILD_VIEW_HEADER_HEIGHT}px))`,
              })}
            >
              <ProposalsOverview
                onDeselect={handleDeselect}
                onDeselectGroup={handleDeselectGroup}
              />
              {!focusViewSegments.length && (
                <Box sx={elementListColumnSx}>
                  <ElementListHeader
                    selectedTab={listTab}
                    onTabChange={handleTabChange}
                    showActionsTab={showActionsTab}
                    showImportTab={showImportTab}
                    isSearchOpen={isSearchOpen}
                    openSearch={openSearch}
                    fieldProps={searchFieldProps}
                    selectedGroupKey={selectedGroupKey}
                  />
                  <Box
                    sx={mergeSx(
                      elementListFillSx,
                      listTab === 'imports'
                        ? importListScrollWrapperSx
                        : listScrollWrapperSx,
                    )}
                  >
                    <SharedIntersectionObserverProvider>
                      {elementListContent}
                    </SharedIntersectionObserverProvider>
                  </Box>
                </Box>
              )}

              {focusViewSegments.length > 0 && (
                <Box sx={elementListColumnSx}>
                  <FocusViewHeader segments={focusViewSegments} />
                  <Box sx={mergeSx(elementListFillSx, listScrollWrapperSx)}>
                    <SharedIntersectionObserverProvider>
                      <ElementListTree aria-label="Elements">
                        {focusViewItems
                          .filter(projectGuards.is.element)
                          .map((element) => (
                            <ElementTreeRow
                              key={element.id}
                              element={element}
                              depth={1}
                              selectedRow={selectedElementId ?? null}
                              onSelect={() => undefined}
                            />
                          ))}
                      </ElementListTree>
                      <ListFiller
                        onClick={handleDeselect}
                        headerHeight={FOCUS_VIEW_HEADER_HEIGHT}
                      />
                      <ElementListFooter />
                    </SharedIntersectionObserverProvider>
                  </Box>
                </Box>
              )}

              <Box>
                <ExpressionInputPanel />
              </Box>
            </Box>
          </ElementListGroupBarScaleProvider>
        </ElementListMaxValuesProvider>
      </MainLayout.Content>
    </MainLayout>
  );
};

export default ElementsView;
